import { CurrencyPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AutomaticPaymentToggleComponent } from 'app/event/payment/automatic-payment-toggle/automatic-payment-toggle.component';
import { EventMenuSearchFilterMapper } from '../menus/event-search-filter/event-menu-search-filter-mapper';
import { PropertyLookupHttpService } from '../shared/lookup/property-lookup/property-lookup.http.service';
import { PropertyLookupStore } from '../shared/lookup/property-lookup/property-lookup.store';
import { SharedModule } from '../shared/shared.module';
import { AddEventButtonBarComponent } from './add-event-button-bar/add-event-button-bar.component';
import { AddEventComponent } from './add-event/add-event.component';
import { AgencyFormComponent } from './agency-form/agency-form.component';
import { AttachmentComponent } from './attachment/attachment.component';
import { AttachmentResolver } from './attachment/attachment.resolver';
import { BasicEventDataComponent } from './basic-event-data/basic-event-data.component';
import { ChildContactFormComponent } from './child-contact-form/child-contact-form.component';
import { ContactFormComponent } from './contact-form/contact-form.component';
import { CompletedUnpaidComponent } from './dashboard/completed-unpaid/completed-unpaid.component';
import { DashboardComponent } from './dashboard/dashboard-page/dashboard.component';
import { MissingAgencyComponent } from './dashboard/missing-agency/missing-agency.component';
import { EventListCardComponent } from './dashboard/recent-events-card/event-list-card.component';
import { EditEventButtonBarComponent } from './edit-event-button-bar/edit-event-button-bar.component';
import { EventListDefaultTableComponent } from './event-list/event-list-default-table/event-list-default-table.component';
import { EventListFinancialComponent } from './event-list/event-list-financial-table/event-list-financial.component';
import { EventListComponent } from './event-list/event-list.component';
import { routes } from './event.routes';
import { EventComponent } from './event/event.component';
import { EventResolver } from './event/event.resolver';
import { MatchingPropertyCandidateComponent } from './matching-property/matching-property-candicate/matching-property-candidate.component';
import { MatchingPropertyListComponent } from './matching-property/matching-property-list/matching-property-list.component';
import { MatchingPropertyComponent } from './matching-property/matching-property.component';
import { DateRoomTypeSummaryComponent } from './overview/date-room-type-summary/date-room-type-summary.component';
import { HeaderSummaryComponent } from './overview/header-summary/header-summary.component';
import { OverviewPageComponent } from './overview/overview-page/overview-page.component';
import { OverviewResolver } from './overview/overview.resolver';
import { StaySummaryComponent } from './overview/stay-summary/stay-summary.component';
import { TransactionResolver } from './overview/transaction.resolver';
import { AddAdjustmentComponent } from './payment/add-adjustment/add-adjustment.component';
import { AddPaymentComponent } from './payment/add-payment/add-payment.component';
import { AddPrepaymentComponent } from './payment/add-prepayment/add-prepayment.component';
import { AdjustmentTypeCreditResolver } from './payment/adjustment-type-credit.resolver';
import { AdjustmentTypeDebitResolver } from './payment/adjustment-type-debit.resolver';
import { AdjustmentResolver } from './payment/adjustment.resolver';
import { CommissionComponent } from './payment/commission/commission.component';
import { FinalPaymentStatusResolver } from './payment/final-payment-status.resolver';
import { LatestPaymentResolver } from './payment/latest-payment.resolver';
import { PaymentAdjustmentEquationComponent } from './payment/payment-adjustment-calculation/payment-adjustment-equation.component';
import { PaymentModifierDisplayComponent } from './payment/payment-modifier-display-calculation/payment-modifier-display.component';
import { PaymentRecordComponent } from './payment/payment-record/payment-record.component';
import { PaymentSummaryComponent } from './payment/payment-summary/payment-summary.component';
import { PaymentsResolver } from './payment/payments.resolver';
import { LatestPrepaymentResolver } from './payment/prepayment.resolver';
import { PaymentReservationsComponent } from './payment/reservations/payment-reservations.component';
import { PaymentRsvpComponent } from './payment/rsvp/payment-rsvp.component';
import { PropertyAddComponent } from './property-add/property-add.component';
import { PropertyLookupFormComponent } from './property-lookup-form/property-lookup-form.component';
import { PropertyPopupComponent } from './property-popup/property-popup.component';
import { PropertySearchResultsComponent } from './property-search-results/property-search-results.component';
import { PropertySearchComponent } from './property-search/property-search.component';
import { RatePlanCodeComponent } from './rate-plan-code/rate-plan-code/rate-plan-code.component';
import { ReservationBulkComponent } from './reservations/reservation-bulk/reservation-bulk.component';
import { ReservationButtonBarComponent } from './reservations/reservation-button-bar/reservation-button-bar.component';
import { CanDeactiveReservationDetailTable } from './reservations/reservation-detail-table/reservation-detail-can-deactive';
import { ReservationDetailTableComponent } from './reservations/reservation-detail-table/reservation-detail-table.component';
import { ReservationDetailResolver } from './reservations/reservation-detail.resolver';
import { ReservationFilterComponent } from './reservations/reservation-filter/reservation-filter.component';
import { ReservationFormComponent } from './reservations/reservation-form/reservation-form.component';
import { ReservationModalComponent } from './reservations/reservation-modal/reservation-modal.component';
import { ReservationPageComponent } from './reservations/reservation-page/reservation-page.component';
import { ReservationSummaryTableComponent } from './reservations/reservation-summary-table/reservation-summary-table.component';
import { ReservationCompareResolver } from './reservations/reservation.compare.resolver';
import { ReservationResolver } from './reservations/reservation.resolver';
import { TransientModalComponent } from './reservations/transient-modal/transient-modal.component';
import { TransientReservationComponent } from './reservations/transient-reservation/transient-reservation.component';
import { TransientSearchComponent } from './reservations/transient-search/transient-search.component';
import { TransientTableComponent } from './reservations/transient-table/transient-table.component';
import { AddRsvpComponent } from './rsvp/add-rsvp/add-rsvp.component';
import { RsvpBulkComponent } from './rsvp/rsvp-bulk/rsvp-bulk.component';
import { RsvpCompareResolver } from './rsvp/rsvp-compare.resolver';
import { RsvpCompareComponent } from './rsvp/rsvp-compare/rsvp-compare.component';
import { RsvpDetailResolver } from './rsvp/rsvp-detail.resolver';
import { RsvpFormComponent } from './rsvp/rsvp-form/rsvp-form.component';
import { RsvpModalComponent } from './rsvp/rsvp-modal/rsvp-modal.component';
import { RsvpComponent } from './rsvp/rsvp-page/rsvp-page.component';
import { RsvpSummaryComponent } from './rsvp/rsvp-summary/rsvp-summary.component';
import { RsvpResolver } from './rsvp/rsvp.resolver';
import { AdjustmentTypeHttpService } from './service/adjustment-type/adjustment-type.http-service';
import { AdjustmentTypeStore } from './service/adjustment-type/adjustment-type.store';
import { AdjustmentHttpService } from './service/adjustment.http.service';
import { AdjustmentStore } from './service/adjustment.store';
import { AttachmentHttpService } from './service/attachment/attachment.http.service';
import { AttachmentStore } from './service/attachment/attachment.store';
import { ChainBrandHttpService } from './service/chainbrand/chain-brand.http-service';
import { ChainBrandStore } from './service/chainbrand/chain-brand.store';
import { ChainResolver } from './service/chainbrand/chain.resolver';
import { CommonDataHttpService } from './service/common-data/common-data.http-service';
import { CommonDataStore } from './service/common-data/common-data.store';
import { CountryResolver } from './service/common-data/country.resolver';
import { CurrencyResolver } from './service/common-data/currency.resolver';
import { CompletedUnpaidDashboardStore } from './service/completed-unpaid-dashboard.store';
import { DashboardTotalsHttpService } from './service/dashboard-totals.http.service';
import { EventFilterUtilsService } from './service/event-filter-util-service';
import { EventListStore } from './service/event-list.store';
import { EventSearchFormsStore } from './service/event-search-forms.store';
import { EventSearchParametersStore } from './service/event-search-parameters.store';
import { EventHttpService } from './service/event.http.service';
import { EventStore } from './service/event.store';
import { EventsResolver } from './service/events.resolver';
import { GroupPayConfigHttpService } from './service/grouppay-config.http.service';
import { GroupPayConfigStore } from './service/grouppay-config.store';
import { MatchingPropertyHttpService } from './service/matching-property.http.service';
import { MatchingPropertyStore } from './service/matching-property.store';
import { MissingAgencyDashboardStore } from './service/missing-agency-dashboard.store';
import { OverviewHttpService } from './service/overview.http.service';
import { OverviewStore } from './service/overview.store';
import { PaymentHttpService } from './service/payment.http.service';
import { PaymentStore } from './service/payment.store';
import { PredefinedSearchService } from './service/predefined-search.service';
import { PropertyHttpService } from './service/property/property.http.service';
import { PropertyStore } from './service/property/property.store';
import { ReservationCompareService } from './service/reservation-compare-http-service';
import { ReservationCompareStore } from './service/reservation-compare-store';
import { ReservationService } from './service/reservation-http-service';
import { ReservationStore } from './service/reservation-store';
import { RsvpHttpService } from './service/rsvp.http.service';
import { RsvpStore } from './service/rsvp.store';
import { TransientReservationService } from './service/transient-reservation-http-service';
import { TransientReservationStore } from './service/transient-reservation-store';
import { BulkActionPopupComponent } from './bulk-action/bulk-action-popup/bulk-action-popup.component';
import { EventCollisionService } from './event-collision/event-collision.service';
import { EventMatchComponent } from './event-match/event-match.component';
import { EventMatchLogStore } from './service/event-match-log.store';
import { EventMatchLogHttpService } from './service/event-match-log.http.service';

@NgModule({
  imports: [
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forChild(routes)
  ],
  declarations: [
    AddEventComponent,
    BasicEventDataComponent,
    ContactFormComponent,
    PropertyLookupFormComponent,
    EventListComponent,
    EventListDefaultTableComponent,
    EventListFinancialComponent,
    EventComponent,
    CommissionComponent,
    PaymentReservationsComponent,
    ChildContactFormComponent,
    HeaderSummaryComponent,
    StaySummaryComponent,
    AddPaymentComponent,
    AttachmentComponent,
    RsvpComponent,
    RsvpSummaryComponent,
    RsvpBulkComponent,
    AddRsvpComponent,
    ReservationFilterComponent,
    ReservationPageComponent,
    ReservationSummaryTableComponent,
    ReservationDetailTableComponent,
    PaymentRecordComponent,
    PaymentAdjustmentEquationComponent,
    PaymentModifierDisplayComponent,
    AutomaticPaymentToggleComponent,
    DashboardComponent,
    CompletedUnpaidComponent,
    MissingAgencyComponent,
    EventListCardComponent,
    ReservationButtonBarComponent,
    ReservationModalComponent,
    ReservationBulkComponent,
    ReservationFormComponent,
    RsvpCompareComponent,
    TransientModalComponent,
    TransientTableComponent,
    TransientSearchComponent,
    TransientReservationComponent,
    EditEventButtonBarComponent,
    AddEventButtonBarComponent,
    AddPrepaymentComponent,
    PaymentSummaryComponent,
    AgencyFormComponent,
    AddAdjustmentComponent,
    RsvpModalComponent,
    RsvpFormComponent,
    OverviewPageComponent,
    DateRoomTypeSummaryComponent,
    PropertyPopupComponent,
    PropertyAddComponent,
    PropertySearchComponent,
    PropertySearchResultsComponent,
    PaymentRsvpComponent,
    RatePlanCodeComponent,
    MatchingPropertyComponent,
    MatchingPropertyListComponent,
    MatchingPropertyCandidateComponent,
    BulkActionPopupComponent,
    EventMatchComponent
  ],
  providers: [
    AttachmentResolver,
    AttachmentStore,
    EventStore,
    EventListStore,
    EventHttpService,
    EventsResolver,
    EventResolver,
    AttachmentHttpService,
    PropertyLookupHttpService,
    PropertyLookupStore,
    ReservationService,
    ReservationResolver,
    ReservationStore,
    ReservationCompareService,
    ReservationCompareResolver,
    ReservationCompareStore,
    PaymentStore,
    PaymentHttpService,
    RsvpStore,
    RsvpHttpService,
    RsvpCompareResolver,
    CurrencyPipe,
    AdjustmentStore,
    AdjustmentHttpService,
    AdjustmentResolver,
    LatestPaymentResolver,
    PaymentsResolver,
    GroupPayConfigStore,
    GroupPayConfigHttpService,
    EventSearchParametersStore,
    CompletedUnpaidDashboardStore,
    MissingAgencyDashboardStore,
    MatchingPropertyStore,
    DashboardTotalsHttpService,
    MatchingPropertyHttpService,
    CanDeactiveReservationDetailTable,
    EventFilterUtilsService,
    EventMenuSearchFilterMapper,
    EventSearchFormsStore,
    PredefinedSearchService,
    TransientReservationStore,
    TransientReservationService,
    LatestPrepaymentResolver,
    CommonDataStore,
    CommonDataHttpService,
    CountryResolver,
    CurrencyResolver,
    AdjustmentTypeStore,
    AdjustmentTypeHttpService,
    AdjustmentTypeCreditResolver,
    AdjustmentTypeDebitResolver,
    RsvpResolver,
    OverviewResolver,
    RsvpDetailResolver,
    ReservationDetailResolver,
    PropertyStore,
    PropertyHttpService,
    TranslateService,
    TransactionResolver,
    OverviewStore,
    OverviewHttpService,
    ChainResolver,
    ChainBrandStore,
    ChainBrandHttpService,
    FinalPaymentStatusResolver,
    EventCollisionService,
    EventMatchLogStore,
    EventMatchLogHttpService
  ]
})
export class EventModule {
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/event.module.ts